import {
  BonusRatioChangeGrantDetail,
  CorporateActionDetails,
  CorporateActionParams,
  RepricingGrantPreviewDetail,
  SplitGrantDetails,
} from "../types/CompanyProfile";
import { CustomResponse } from "../types/Grant";
import { CompanyProfileDetails } from "../types/SiteSettings";
import optionsApi from "./optionsApi";

export interface RegionalGrantRule {
  country: string;
  minCliffMonths?: number;
  minStrikePrice?: number;
  strikePriceOptional?: boolean;
}

export interface RegionalGrantRuleCatalog {
  rules: RegionalGrantRule[];
  loadedAt: string;
}

export async function getRegionalGrantRules(): Promise<
  CustomResponse<RegionalGrantRuleCatalog>
> {
  return optionsApi
    .get("v1/company/regional-grant-rules")
    .then((res) => res.data);
}

export async function getCompanyProfileDetails(): Promise<
  CustomResponse<CompanyProfileDetails>
> {
  return optionsApi.get(`v1/company/details`).then((res) => res.data);
}

export async function updateCompanyProfile(
  companyProfileDetails: CompanyProfileDetails
): Promise<any> {
  return optionsApi
    .post("v1/company/updateProfile", companyProfileDetails)
    .then((res) => res.data);
}

export async function createEsopCompany(createJson: any): Promise<string> {
  return optionsApi
    .post(`v1/company/create`, createJson)
    .then((res) => res.data);
}

export async function getAllCorporateActions(companyId: string): Promise<{
  corporateActions: CorporateActionDetails[];
  userHasLimitedAccessToCompany: boolean;
}> {
  return optionsApi.get(`v1/corporateAction/all`).then((res) => res.data.data);
}

export async function generateBonusReport(
  corporateActionDetails: CorporateActionDetails
): Promise<{ base64String: string }> {
  return optionsApi
    .post(`v1/corporateAction/generateBonusReport`, {
      corporateActionDetails,
    })
    .then((res) => res.data.data);
}

export async function approveBonus(
  corporateActionId: string
): Promise<{ base64String: string }> {
  return optionsApi
    .post(`v1/corporateAction/approveBonus`, {
      corporateActionId,
    })
    .then((res) => res.data.data);
}

export async function discardCorporateAction(
  corporateActionId: string
): Promise<{ base64String: string }> {
  return optionsApi
    .delete(`v1/corporateAction/discard?corporateActionId=${corporateActionId}`)
    .then((res) => res.data.data);
}

export async function uploadDocumentTemplate(documentDetails: {
  file: Blob;
  key: string;
  contentType: string;
  templateName: string;
  companyId: string;
  templateType: string;
  entityType: string;
  entityId: string;
}): Promise<any> {
  const formData = new FormData();
  formData.append("file", documentDetails.file, documentDetails.templateName);
  formData.append("key", documentDetails.key);
  formData.append("contentType", documentDetails.contentType);
  formData.append("templateName", documentDetails.templateName);
  formData.append("companyId", documentDetails.companyId);
  formData.append("templateType", documentDetails.templateType);
  formData.append("entityType", documentDetails.entityType);
  formData.append("entityId", documentDetails.entityId);
  return optionsApi
    .post(`v1/template/documentTemplate`, formData)
    .then((res) => res.data.data);
}

export async function getAllGrantDetailsForCorporateActions(
  id: string,
  state: string
): Promise<SplitGrantDetails[]> {
  return optionsApi
    .get(
      `v1/corporateAction/getGrantsForCorporateAction/id=${id}/state=${state}`
    )
    .then((res) => res.data.data);
}

export async function createCorporateAction(params: CorporateActionDetails) {
  return optionsApi
    .post(`v1/corporateAction/create`, params)
    .then((res) => res.data.data);
}

export async function approveSplit(corporateActionId: string) {
  return optionsApi
    .post(`v1/corporateAction/approveSplit`, {
      corporateActionId,
    })
    .then((res) => res.data.data);
}

export async function previewBonusRatioChange(
  corporateActionDetails: CorporateActionDetails
): Promise<{
  corporateActionId: string;
  grantDetails: BonusRatioChangeGrantDetail[];
}> {
  return optionsApi
    .post(`v1/corporateAction/bonusRatioChange/preview`, {
      corporateActionDetails,
    })
    .then((res) => res.data.data);
}

export async function approveBonusRatioChange(
  corporateActionId: string
): Promise<string> {
  return optionsApi
    .post(`v1/corporateAction/bonusRatioChange/approve`, {
      corporateActionId,
    })
    .then((res) => res.data.data);
}

export async function discardBonusRatioChange(
  corporateActionId: string
): Promise<string> {
  return optionsApi
    .delete(
      `v1/corporateAction/bonusRatioChange/discard?corporateActionId=${corporateActionId}`
    )
    .then((res) => res.data.data);
}

export async function previewOptionsRepricing(
  corporateActionParams: CorporateActionParams
): Promise<{
  corporateActionId: string;
  grantDetails: RepricingGrantPreviewDetail[];
  base64String?: string;
}> {
  return optionsApi
    .post(`v1/corporateAction/optionsRepricing/preview`, {
      corporateActionParams,
    })
    .then((res) => res.data.data);
}

export async function approveOptionsRepricing(
  corporateActionId: string
): Promise<{ reportUrl?: string }> {
  return optionsApi
    .post(`v1/corporateAction/optionsRepricing/approve`, { corporateActionId })
    .then((res) => res.data.data);
}

export async function getRepricingHistory(): Promise<CorporateActionParams[]> {
  return optionsApi
    .get(`v1/corporateAction/optionsRepricing/history`)
    .then((res) => res.data.data);
}

export async function updateOptionsRepricingSummaryVisibility(params: {
  corporateActionId: string;
  showOptionsRepricingSummary: boolean;
}): Promise<{ showOptionsRepricingSummary: boolean }> {
  return optionsApi
    .put(`v1/corporateAction/optionsRepricing/summaryVisibility`, params)
    .then((res) => res.data.data);
}

export async function overrideGrantRepricing(params: {
  corporateActionId: string;
  grantId: string;
  newStrikePrice: number;
}): Promise<string> {
  return optionsApi
    .put(`v1/corporateAction/optionsRepricing/overrideGrant`, params)
    .then((res) => res.data.data);
}
